import { useState } from 'react';
import { Link } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import { Alert, Button, Card, EmptyState, ErrorState, Loading } from '../../shared/ui/components';
import { VerseField } from '../bible/VerseField';
import { useAsync } from '../../shared/ui/useAsync';
import { useMe } from '../session/SessionProvider';
import { AudioRecorder } from './AudioRecorder';

type Day = { day: string; verseRef: string | null; prayer: string | null; reflection: string | null; hasAudio?: boolean };
const label = (iso: string) => {
  const [y, m, d] = iso.split('-').map(Number);
  const s = new Date(y!, m! - 1, d!).toLocaleDateString('es', { weekday: 'long', day: 'numeric', month: 'long' });
  return s.charAt(0).toUpperCase() + s.slice(1);
};

/** La administración y los líderes de ministerio preparan, entre todos, el "Hoy" de los próximos días. */
export function DailyPlannerPage() {
  const me = useMe();
  const churchId = me.activeChurchId;
  const data = useAsync((signal) => (churchId ? api.get<{ items: Day[] }>(`/devotional/churches/${churchId}/daily?days=14`, signal).then((r) => r.items) : Promise.resolve([])), [churchId]);
  const [open, setOpen] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  if (!churchId) return <EmptyState title="Elige primero una iglesia en el selector de arriba." />;

  return (
    <div className="page page--narrow">
      <p className="small">
        <Link to="/app/devocional?vista=administrar">← Planes de lectura</Link>
      </p>
      <header className="page__header">
        <h1>El «Hoy» de la iglesia</h1>
        <p className="muted">Lo que verán todos al abrir KEORA cada día. Lo preparan entre todos los líderes de cada ministerio y la administración. Si un día queda vacío, aparece un versículo de la lista de KEORA.</p>
      </header>
      {error ? <Alert>{error}</Alert> : null}
      {saved ? <Alert tone="success">{saved}</Alert> : null}
      {data.status === 'loading' ? <Loading /> : null}
      {data.status === 'error' ? <ErrorState error={data.error} onRetry={data.reload} /> : null}
      {data.status === 'ready'
        ? data.data.map((d) => (
            <Card
              key={d.day}
              title={label(d.day)}
              action={
                open === d.day ? null : (
                  <Button variant="ghost" onClick={() => setOpen(d.day)}>
                    {d.verseRef || d.prayer || d.reflection ? 'Editar' : 'Preparar'}
                  </Button>
                )
              }
            >
              {open === d.day ? (
                <form
                  className="form"
                  onSubmit={async (e) => {
                    e.preventDefault();
                    const f = new FormData(e.currentTarget);
                    const t = (k: string) => String(f.get(k) ?? '').trim() || null;
                    setBusy(true);
                    setError(null);
                    setSaved(null);
                    try {
                      await api.put(`/devotional/churches/${churchId}/daily/${d.day}`, { verseRef: t('verseRef'), prayer: t('prayer'), reflection: t('reflection') });
                      setOpen(null);
                      setSaved(`${label(d.day)} quedó listo.`);
                      data.reload();
                    } catch (err) {
                      setError(errorMessage(err));
                    } finally {
                      setBusy(false);
                    }
                  }}
                >
                  <VerseField label="Versículo del día" name="verseRef" defaultValue={d.verseRef ?? ''} placeholder="Salmos 23:1" idPrefix={`vr-${d.day}`} />
                  <div className="field">
                    <label htmlFor={`refl-${d.day}`}>Reflexión (opcional)</label>
                    <textarea id={`refl-${d.day}`} name="reflection" rows={3} maxLength={4000} defaultValue={d.reflection ?? ''} />
                  </div>
                  <div className="field">
                    <label htmlFor={`pray-${d.day}`}>Oración del día (opcional)</label>
                    <textarea id={`pray-${d.day}`} name="prayer" rows={3} maxLength={2000} defaultValue={d.prayer ?? ''} />
                  </div>
                  <div className="actions">
                    <Button type="submit" busy={busy}>
                      Guardar
                    </Button>
                    <Button type="button" variant="ghost" onClick={() => setOpen(null)}>
                      Cancelar
                    </Button>
                  </div>
                </form>
              ) : d.verseRef || d.prayer || d.reflection ? (
                <p className="small">
                  {d.verseRef ? <strong>{d.verseRef}</strong> : <span className="muted">Versículo de la lista</span>}
                  {d.reflection ? ' · con reflexión' : ''}
                  {d.prayer ? ' · con oración' : ''}
                </p>
              ) : (
                <p className="small muted">Sin preparar: se mostrará un versículo de la lista.</p>
              )}
              <AudioRecorder churchId={churchId} day={d.day} hasAudio={Boolean(d.hasAudio)} onChange={data.reload} />
            </Card>
          ))
        : null}
    </div>
  );
}
